import { GameSettings, GameState } from '../interfaces/game.interface';
import { RoomStatus } from '../interfaces/room.interface';

// ============================================
// Client to Server - Room Management DTOs
// ============================================

/**
 * Create a new game room
 * This DTO should have validation on the backend
 * Note: hostId is sent by client but server uses socket.id for security
 */
export interface CreateRoomDto {
  roomName: string; // 3-20 characters
  password?: string; // Optional password for private rooms (min 4 characters)
  username?: string; // Display name for the host player
  settings?: Partial<GameSettings>; // Optional overrides; server fills in defaults
}

/**
 * Join a room using its joinCode (capability-based auth).
 * Used for public rooms (joinCode from list) and shared-link joins.
 * No password needed — possessing the joinCode IS the authorisation.
 */
export interface JoinRoomDto {
  joinCode: string;
}

/**
 * Verify password for a private room.
 * On success the server adds the player to the room and returns the joinCode.
 */
export interface VerifyRoomPasswordDto {
  roomId: string; // Public room id (always visible in the room list)
  password: string;
}

/**
 * Leave a room
 * Note: playerId is sent by client but server uses socket.id for security
 */
export interface LeaveRoomDto {
  roomId: string;
}

// ============================================
// Server to Client - Room State DTOs
// ============================================

/**
 * Room state update sent to clients.
 * `joinCode` is only populated for public rooms or when the player is already in the room.
 * Private rooms in the lobby list will NOT carry a joinCode.
 */
export interface RoomStateDto {
  id: string;
  joinCode?: string; // Conditionally included (see above)
  roomName: string;
  hostId: string;
  playerIds: string[];
  playerNames: Record<string, string>; // Map of socketId -> display name
  status: RoomStatus;
  hasPassword: boolean; // Indicates if room requires password to join
  createdAt: number;
  startedAt?: number;
  readyPlayers: Record<string, boolean>; // socketId -> isReady
  settings: GameSettings;
  disconnectedPlayers: string[]; // socket IDs of players currently in grace period
}

/**
 * List of available rooms
 */
export interface RoomListDto {
  rooms: RoomStateDto[];
  total: number;
}

/**
 * Player joined room notification
 */
export interface PlayerJoinedRoomDto {
  roomId: string;
  playerId: string;
  playerName: string;
}

/**
 * Player left room notification
 */
export interface PlayerLeftRoomDto {
  roomId: string;
  playerId: string;
}

/**
 * Payload for playerJoined / playerLeft socket broadcasts
 */
export interface PlayerRoomEventDto {
  playerId: string;
  room: RoomStateDto;
}

/**
 * Payload for the matchStart event sent to all players.
 */
export interface MatchStartDto {
  gameDuration: number;
  playerOrders: Record<string, string[]>;
}

/**
 * Reconnect request — sent by client on reconnect with stored token + joinCode.
 */
export interface ReconnectDto {
  token: string;   // the persistent player token from localStorage
  joinCode: string; // the room's joinCode, so the server knows which room to restore into
}

/**
 * Reconnect success — sent only to the reconnecting client.
 */
export interface ReconnectSuccessDto {
  room: RoomStateDto;    // full room state to hydrate the client
  gameState?: GameState; // present only if the room is IN_PROGRESS
}
